import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useLoyaltyTransactions } from 'features/loyalty-transactions/use-loyalty-transactions';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useEvictAppliedOffer } from 'features/offer/hooks/use-evict-applied-offer';
import { useLoyaltyServiceAppliedOfferEviction } from 'features/offer/hooks/use-loyalty-service-applied-offer-eviction';
import { logPurchase } from 'features/order/utils/analytics';
import { usePaymentContext } from 'features/payment';
import { useStoreContext } from 'features/store';
import { isEqual } from 'lodash-es';

import { ILegacyCartEntry, IServerOrder } from '@rbi-ctg/menu';
import { GetOrderDocument, IDelivery, useUpdateOrderMutation } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useCartAvailabilityCheck } from 'hooks/use-cart-availability-check';
import { useCartHydration } from 'hooks/use-cart-hydration.ts';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useSetResetCartTimeout } from 'hooks/use-set-reset-cart-timeout';
import { useStableCallback } from 'hooks/use-stable-callback';
import { CustomEventNames, EventTypes, logEvent, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useErrorContext } from 'state/errors';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { apolloClient } from 'state/graphql/client';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser, useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';
import { useMainMenuContext } from 'state/menu/main-menu';
import { getServiceModeFromLS, useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { sanitizeCartForReprice } from 'utils/cart';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { IPlaceAddress } from 'utils/geolocation/types';
import logger, { addLoggingContext } from 'utils/logger';

import { useHandleReorder } from './hooks/use-handle-reorder';
import { useUnavailableCartEntries } from './hooks/use-unavailable-cart-entries';
import { preloadedOrder } from './preloaded-order';
import { IOrderContext, IPendingReorder, ISelectOrderStore } from './types';
import { validateCartEntries } from './utils';

// How long to wait for `personalizedOfferSettled` to hold steady before
// trusting it to run legacy CMS-map offer eviction — see that effect below.
const PERSONALIZED_OFFER_EVICTION_DEBOUNCE_MS = 2000;

export const OrderContext = React.createContext<IOrderContext>({} as IOrderContext);
export const useOrderContext = () => useContext(OrderContext);

export const OrderProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const preloaded = useMemo(() => preloadedOrder(), []);

  const { refetchCurrentUser, user } = useAuthContext();
  const billingCountry = user?.details?.isoCountryCode || ISOs.USA;
  const { currencyCode } = getCountryAndCurrencyCodes(billingCountry as ISOs);
  const { getPaymentMethods } = usePaymentContext();
  const { prices, resetStore, selectStore, store, isStoreOpenAndAvailable } = useStoreContext();

  const { isLoading: isOneMenuLoading } = useUnifiedMenuContext();
  const { storeMenuLoading } = useMainMenuContext();
  const { selectServiceMode } = useServiceModeContext();
  const { setCurrentOrderId } = useErrorContext();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const { loyaltyUser } = useLoyaltyUser();
  const enableMinimalCartUi = useMinimalCartUi();
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();

  // selectors
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const loyaltyCmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const incentivesIds = useAppSelector(selectors.loyalty.selectIncentivesIds);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const dispatch = useAppDispatch();

  const loyaltyUserId = loyaltyUser?.id;
  // The personalized-offer queries (use-loyalty-offers.ts) key off the auth
  // loyalty id, not the fetched loyalty profile above — using `loyaltyUserId`
  // here would keep this gate `false` forever if the profile fetch fails or
  // is disabled, even after the offer data has genuinely settled.
  const { loyaltyUserId: authLoyaltyUserId } = useLoyaltyUserId();

  // `offersLoading` reads false both when personalized-offer data has loaded
  // AND when the fetch was never attempted (e.g. `authLoyaltyUserId` not yet
  // resolved on a fresh refresh) — only trust it once the id has resolved.
  //
  // `offersLoading` (useLoyaltyOffers.ts) is an OR of several independent
  // hook `loading` flags, chained across multiple sequential async steps
  // (engine offer-items query -> personalized-data fetch -> CMS map
  // dispatch). It can read `false` for a render or two *between* those
  // steps, not just before the first one starts, so `personalizedOfferSettled`
  // can flicker true->false->true while data is still in flight. The
  // eviction effect below debounces its own use of this value with a
  // `setTimeout` (cancelled/restarted via effect cleanup whenever the raw
  // value changes) instead of trusting it on the render it first flips
  // true — a plain "confirm after one render" via useState was tried and is
  // NOT sufficient (that state update itself lags a render behind the
  // effect that reads it, so a same-render true->false flip could still be
  // read as confirmed-true). See fix-personalized-offer-eviction-race.
  const personalizedOfferSettled = !!authLoyaltyUserId && !offersLoading;

  const { refetch: refetchLoyaltyUserTransaction } = useLoyaltyTransactions({
    skip: !loyaltyUserId,
  });

  const [executeUpdateOrderMutation] = useUpdateOrderMutation();

  const [pendingReorder, setPendingReorder] = useState<IPendingReorder | null>(null);
  const [reordering, setReordering] = useState<boolean>(false);
  const [reorderedOrderId, setReorderedOrderId] = useState<string | null>(null);

  const {
    cartEntries,
    calculateCartTotal,
    addItemToCart,
    emptyCart,
    isCartEmpty,
    removeFromCart,
    repriceCartEntries,
  } = useCart();

  const { removeItemFromMinimalCart } = useMinimalCart();

  const [pendingRecentItem, setPendingRecentItem] = useState<ILegacyCartEntry | null>(null);

  const { prepareCartEntries, isStoreChangeHydrationInProgress } = useCartHydration({
    cartEntries,
    minimalCartEntries,
  });

  const [serverOrder, setServerOrder] = useState<IServerOrder | null>(null);
  const [quoteId, setQuoteId] = useState<string>(preloaded.quoteId || '');
  const [checkoutRequestedSubtotalCents, setCheckoutRequestedSubtotalCents] = useState<
    number | null
  >(null);
  const [checkoutRepriceRequestId, setCheckoutRepriceRequestId] = useState(0);
  const [checkoutRepriceRequestCompletedId, setCheckoutRepriceRequestCompletedId] = useState(0);
  const [shouldSaveDeliveryAddress, setShouldSaveDeliveryAddress] = useState<boolean>(false);
  const [formShouldSaveDeliveryAddress, setFormShouldSaveDeliveryAddress] = useState<boolean>(
    false
  );
  const [deliveryAddress, setDeliveryAddress] = useState<IPlaceAddress | null>(
    preloaded.deliveryAddress || {}
  );
  const [deliveryInstructions, setDeliveryInstructions] = useState<string | undefined>(
    preloaded.deliveryInstructions || ''
  );

  const [fetchingPosData, setFetchingPosData] = useState<boolean>(false);

  const userPhoneNumber = user?.details?.phoneNumber;
  const [orderPhoneNumber, setOrderPhoneNumber] = useState<string>(
    // Initialize with:
    // 1. preloaded number from a previous order using the selected delivery address
    // 2. user's default number
    // 3. or an empty string if neither is available
    () => preloaded.orderPhoneNumber || user?.details?.phoneNumber || ''
  );

  React.useEffect(() => {
    if (orderPhoneNumber) {
      return;
    }
    if (userPhoneNumber) {
      setOrderPhoneNumber(userPhoneNumber);
    }
  }, [userPhoneNumber, orderPhoneNumber]);

  const [pickupDate, setPickupDate] = useState<Date | null>(null);

  // TODO: BKPE-1956 - unavailableCartEntries
  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    cartEntries,
    serverOrder,
    isStoreChangeHydrationInProgress,
  });

  const [
    unavailablePendingRecentItem,
    setUnavailablePendingRecentItem,
  ] = useState<ILegacyCartEntry | null>(null);

  const updateShouldSaveDeliveryAddress = useCallback((shouldSaveDeliveryAddress: boolean) => {
    setDeliveryAddress(previousAddress => {
      if (!previousAddress || previousAddress.shouldSave === shouldSaveDeliveryAddress) {
        return previousAddress;
      }
      return {
        ...previousAddress,
        shouldSave: shouldSaveDeliveryAddress,
      };
    });
  }, []);

  useSetResetCartTimeout({
    storageKey: SyncStorageKey.ORDER_LAST_UPDATE,
    cart: cartEntries,
    resetCartCallback: emptyCart,
  });

  useEffect(() => {
    setCurrentOrderId(serverOrder?.rbiOrderId || '');
  }, [serverOrder, setCurrentOrderId]);

  useEffectOnUpdates(() => {
    // Legacy presence-based eviction against the CMS offers map. When the
    // loyalty service is enabled this is bypassed in favor of absence from the
    // settled applied-offers batch response (see
    // `useLoyaltyServiceAppliedOfferEviction` below). In that mode this map no
    // longer reflects the offers applied through it, so this cleanup must not
    // run against it.
    if (loyaltyServiceOffersEnabled) {
      return;
    }
    // Until personalized-offer data has settled, a missing cmsId in the map
    // means "not fetched yet," not "invalid," so eviction must wait.
    if (!personalizedOfferSettled) {
      return;
    }
    // Debounce: `personalizedOfferSettled` can flicker true while a chained
    // multi-step fetch is still in flight (see its declaration above).
    // Waiting inside a cancellable timeout — rather than trusting the value
    // on the render it first flips true — means a later render that flips it
    // back to unsettled (or changes the applied offers / CMS map) cancels
    // this via the effect's cleanup before it ever fires, with no risk of
    // reading a stale confirmation.
    const timeoutId = setTimeout(() => {
      // Creating a map out of all valid offers in the CMS
      const cmsOffersMap = getCmsOffersMapByCmsId(loyaltyCmsOffers);
      appliedOffers.forEach(({ cartId, cmsId }) => {
        // If the applied offer is not in the cms offers map, should remove it
        const shouldRemoveCartEntry = !cmsId || !cmsOffersMap[cmsId];
        if (shouldRemoveCartEntry && cartId) {
          // Removing offer from cart will also remove it from applied offers
          removeFromCart({ cartId });
        }
      });
    }, PERSONALIZED_OFFER_EVICTION_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // Gate is a real dependency, not just a guard: on an ON→OFF transition the
    // batch eviction goes inert while `loyaltyCmsOffers` may keep its identity,
    // so this legacy cleanup must re-run when the gate turns off. Same for
    // `personalizedOfferSettled`, so a deferred run resumes once settled.
  }, [
    appliedOffers,
    loyaltyCmsOffers,
    loyaltyServiceOffersEnabled,
    personalizedOfferSettled,
    removeFromCart,
  ]);

  // Two-store offer eviction: clears the legacy cart entry / Redux applied-offer
  // record and the matching minimal-cart discount/incentive entry — otherwise a
  // backend-delisted offer stays visible and priced in the minimal cart. The
  // minimal row is cleared regardless of the minimal-cart UI experiment because
  // it is persisted whenever the unified menu is enabled. Stable identity so the
  // eviction effect keys only on the settled batch, not on cart churn.
  const evictAppliedOffer = useEvictAppliedOffer({
    removeFromCart,
    removeItemFromMinimalCart,
    minimalCartEntries,
  });

  // Loyalty-service eviction: keys on absence from the settled applied-offers
  // batch response. Inert unless the loyalty service is enabled.
  useLoyaltyServiceAppliedOfferEviction({ evictAppliedOffer });

  useEffect(() => {
    if (incentivesIds.size > 0 && !!cartEntries.length && !!Object.keys(appliedOffers).length) {
      validateCartEntries(cartEntries, appliedOffers, incentivesIds, removeFromCart);
    }
  }, [appliedOffers, cartEntries, incentivesIds, removeFromCart]);

  const logCartStoreAndTimeout = useCallback(
    (resetStoreTimeout: number, timeSinceLastVisit: number) => {
      const storeDetails = {
        cartEntriesTotal: cartEntries.length,
        storeId: store._id,
        itemNames: cartEntries.map((entry: any) => entry.name),
      };

      const { cartEntriesTotal, storeId, itemNames } = storeDetails;

      logEvent(CustomEventNames.SESSION_RESET_FROM_INACTIVITY, EventTypes.Other, {
        storeDetails: `cartEntriesTotal: ${cartEntriesTotal}, storeId: ${storeId}, itemNames: ${JSON.stringify(
          itemNames
        )} `,
        resetStoreTimeoutSeconds: resetStoreTimeout,
        hoursSinceLastVisit: parseInt((timeSinceLastVisit / (1000 * 60 * 60)).toFixed(1)),
      });
    },
    [cartEntries, store._id]
  );

  const clearCartStoreServiceModeTimeout = useCallback(() => {
    selectServiceMode(null);
    setUnavailableCartEntries([]);
    emptyCart();
    resetStore();
    syncStorage.remove(SyncStorageKey.LAST_TIME_STORE_UPDATED);
  }, [emptyCart, resetStore, selectServiceMode, setUnavailableCartEntries]);

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);

  const _alertOrderDeliveryMinimum = useCallback(() => {
    const cartTotal = calculateCartTotal();
    const serviceMode = getServiceModeFromLS();
    const isDelivery = serviceMode === ServiceMode.DELIVERY;
    if (cartEntries.length > 0 && isDelivery && cartTotal < checkoutDeliveryPriceMinimum) {
      logEvent(CustomEventNames.CHECKOUT_DELIVERY_MINIMUM_NOT_REACHED, EventTypes.Other, {
        total: String(cartTotal),
      });

      logRBIEvent(
        {
          name: CustomEventNames.DELIVERY_MINIMUM_NOT_REACHED,
          type: EventTypes.Other,
          attributes: {
            subtotal: cartTotal,
            deliveryMinimum: checkoutDeliveryPriceMinimum,
          },
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [calculateCartTotal, cartEntries.length, checkoutDeliveryPriceMinimum]);
  const alertOrderDeliveryMinimum = useStableCallback(_alertOrderDeliveryMinimum);

  const queryOrder = useCallback(async (rbiOrderId: string): Promise<IServerOrder | null> => {
    const { data, errors } = await apolloClient.query<{ order: IServerOrder }>({
      fetchPolicy: 'network-only',
      query: GetOrderDocument,
      variables: {
        id: rbiOrderId,
      },
    });

    if (errors) {
      // eslint-disable-next-line no-undef
      logger.error({ errors, message: 'Error querying order' });
      throw errors;
    }

    return data?.order ?? null;
  }, []);

  // commit was being fired twice, which was firing onCommitSuccess twice
  // the lastPurchaseOrderRId checks if the same order is being committed twice
  // where we set .current = rbiOrderID is on the first run, so the second run will return early
  const lastPurchaseOrderId = useRef<string>(undefined);
  const _onCommitSuccess = useCallback(
    async (remoteOrder: IServerOrder) => {
      if (lastPurchaseOrderId.current === remoteOrder.rbiOrderId) {
        return;
      }
      if (remoteOrder.cart?.serviceMode === ServiceMode.DELIVERY) {
        setShouldSaveDeliveryAddress(false);
        updateShouldSaveDeliveryAddress(false);
      }
      lastPurchaseOrderId.current = remoteOrder.rbiOrderId;
      setCheckoutRepriceRequestId(0);
      setCheckoutRepriceRequestCompletedId(0);
      // Clear delivery quote so the next order does not inherit quote staleness
      // from the previous commit (can leave Checkout V2 loading after address change).
      setQuoteId('');
      emptyCart();

      // TODO: improve IServerOrder and IDelivery types to consume directly from generated types
      const remoteOrderDelivery: IDelivery | null = remoteOrder.delivery as IDelivery;
      const quotedFeeCents = remoteOrderDelivery?.quotedFeeCents || 0;
      const deliveryFeeCents = remoteOrderDelivery?.feeCents || 0;
      const deliveryFeeDiscountCents = remoteOrderDelivery?.feeDiscountCents || 0;
      const deliveryGeographicalFeeCents = remoteOrderDelivery?.geographicalFeeCents || 0;
      const deliveryServiceFeeCents = remoteOrderDelivery?.serviceFeeCents || 0;
      const deliverySmallCartFeeCents = remoteOrderDelivery?.smallCartFeeCents || 0;
      const baseDeliveryFeeCents = remoteOrderDelivery?.baseDeliveryFeeCents || 0;
      const deliverySurchargeFeeCents = remoteOrderDelivery?.deliverySurchargeFeeCents || 0;
      logPurchase(
        cartEntries,
        store,
        (remoteOrder.cart?.serviceMode ?? getServiceModeFromLS()) as ServiceMode,
        remoteOrder,
        {
          currencyCode,
          quotedFeeCents,
          baseDeliveryFeeCents,
          totalDeliveryOrderFeesCents:
            baseDeliveryFeeCents +
            deliverySurchargeFeeCents +
            deliveryServiceFeeCents +
            deliverySmallCartFeeCents +
            deliveryGeographicalFeeCents -
            deliveryFeeDiscountCents,
          deliveryFeeCents:
            deliveryFeeCents -
            deliveryFeeDiscountCents -
            deliveryGeographicalFeeCents -
            deliveryServiceFeeCents -
            deliverySmallCartFeeCents,
          deliverySurchargeFeeCents,
          deliveryFeeDiscountCents,
          deliveryGeographicalFeeCents,
          deliveryServiceFeeCents,
          deliverySmallCartFeeCents,
        }
      );
      setServerOrder(remoteOrder);

      try {
        await Promise.all([
          // refresh the user's payment methods stored
          // in state, including gift cards
          getPaymentMethods({ source: 'order-commit-success' }),
          // refresh loyalty user points and recent transactions
          loyaltyUserId ? refetchLoyaltyUser() : Promise.resolve(),
          loyaltyUserId ? refetchLoyaltyUserTransaction() : Promise.resolve(),
          // refresh unique purchases
          refetchCurrentUser(),
          // refetch loyalty rewards
          dispatch(actions.loyalty.setShouldRefetchRewards(true)),
        ]);
      } catch (error) {
        logger.error({ error, message: 'Error after commit success' });
      }
    },
    [
      emptyCart,
      cartEntries,
      store,
      currencyCode,
      updateShouldSaveDeliveryAddress,
      getPaymentMethods,
      loyaltyUserId,
      refetchLoyaltyUser,
      refetchLoyaltyUserTransaction,
      refetchCurrentUser,
      dispatch,
      setQuoteId,
    ]
  );
  const onCommitSuccess = useStableCallback(_onCommitSuccess);

  const getAndRefreshServerOrder = useCallback(
    async (id: string): Promise<IServerOrder | null> => {
      try {
        const order = await queryOrder(id);
        setServerOrder(order);
        return order;
      } catch (error) {
        logger.error({ error, message: 'Error refreshing order' });
      }
      return null;
    },
    [queryOrder]
  );

  const fireOrderInXSeconds = useCallback<IOrderContext['fireOrderInXSeconds']>(
    async ({ rbiOrderId, timeInSeconds }) => {
      const { data, errors } = await executeUpdateOrderMutation({
        variables: {
          input: {
            fireOrderIn: timeInSeconds,
            rbiOrderId,
          },
        },
      });

      if (errors) {
        logger.error({ errors, message: 'Error updating order.' });
        throw errors;
      }

      return getAndRefreshServerOrder(data?.updateOrder?.rbiOrderId || rbiOrderId);
    },
    [executeUpdateOrderMutation, getAndRefreshServerOrder]
  );

  const clearServerOrder = useCallback(() => {
    setServerOrder(null);
  }, []);

  // make sure we persist everything!
  useEffect(() => {
    syncStorage.setObject(SyncStorageKey.ORDER, {
      deliveryAddress,
      deliveryInstructions,
      quoteId,
      orderPhoneNumber,
    });
  }, [deliveryInstructions, orderPhoneNumber, deliveryAddress, quoteId]);

  // Configure the logger to hold some information
  const serverOrderRbiOrderId = serverOrder?.rbiOrderId;
  useEffect(() => {
    const storePosVendor = store?.pos?.vendor?.toLowerCase();

    const serviceMode = getServiceModeFromLS();
    const extras = {
      serviceMode,
      storePosVendor,
    };

    addLoggingContext({
      ...extras,
      rbiOrderId: serverOrderRbiOrderId,
    });
  }, [cartEntries, serverOrderRbiOrderId, store]);

  const repriceCartEntriesRef = useRef<typeof repriceCartEntries>(undefined);
  repriceCartEntriesRef.current = repriceCartEntries;

  useEffect(() => {
    if (
      isStoreChangeHydrationInProgress ||
      isOneMenuLoading ||
      storeMenuLoading ||
      !prices ||
      !cartEntries?.length ||
      !repriceCartEntriesRef.current
    ) {
      return;
    }

    const entriesForReprice = sanitizeCartForReprice(cartEntries);
    const repricedCartEntries = repriceCartEntriesRef.current(entriesForReprice);

    if (!isEqual(repricedCartEntries, cartEntries)) {
      dispatch(actions.ordering.repriceCartEntries(repricedCartEntries));
    }
  }, [
    cartEntries,
    dispatch,
    isOneMenuLoading,
    isStoreChangeHydrationInProgress,
    prices,
    storeMenuLoading,
  ]);

  const handleReorder = useHandleReorder({
    addToCart: addItemToCart,
    setPendingReorder,
    setReordering,
    setUnavailableCartEntries,
    setReorderedOrderId,
  });

  const resetPendingReorder = useCallback(() => {
    setPendingReorder(null);
    setReordering(false);
    setReorderedOrderId(null);
  }, []);

  const clearUserOwnedOrderState = useCallback(() => {
    selectServiceMode(null);
    resetStore();
    emptyCart();
    dispatch(actions.ordering.resetMinimalCart());
    setServerOrder(null);
    resetPendingReorder();
    setPendingRecentItem(null);
    setUnavailablePendingRecentItem(null);
    setUnavailableCartEntries([]);
    setFetchingPosData(false);
    setQuoteId('');
    setCheckoutRequestedSubtotalCents(null);
    setCheckoutRepriceRequestId(0);
    setCheckoutRepriceRequestCompletedId(0);
    setShouldSaveDeliveryAddress(false);
    setFormShouldSaveDeliveryAddress(false);
    setDeliveryAddress(null);
    setDeliveryInstructions('');
    setOrderPhoneNumber('');
    setPickupDate(null);
    shouldHandleReorderRef.current = false;
  }, [
    dispatch,
    emptyCart,
    resetPendingReorder,
    resetStore,
    selectServiceMode,
    setUnavailableCartEntries,
  ]);

  // Check for unavailable entries in the Cart ( not the server order entries ) after a store change.
  useCartAvailabilityCheck({
    cartEntries,
    isStoreChangeHydrationInProgress,
    appliedOffers,
    loyaltyUserId,
  });

  const selectOrderStore = useCallback(
    async ({
      newStore,
      callback,
      requestedServiceMode,
      skipChangeStoreMessage,
    }: ISelectOrderStore) => {
      setFetchingPosData(true);
      const finalServiceMode = requestedServiceMode ?? ServiceMode.DRIVE_THRU;
      const previousStoreNumber = store.number ?? null;
      const nextStoreNumber = newStore.number ?? null;
      const shouldRequestCheckoutReprice = Boolean(
        previousStoreNumber && nextStoreNumber && previousStoreNumber !== nextStoreNumber
      );
      const hasCartItems = !isCartEmpty || (enableMinimalCartUi && minimalCartEntries.length > 0);
      const shouldRequestDeliveryReprice = Boolean(
        finalServiceMode === ServiceMode.DELIVERY && hasCartItems
      );
      await selectStore({
        skipChangeStoreMessage,
        sanityStore: newStore,
        hasCartItems,
        callback: async () => {
          //On the Offers Details screen the user can choose an Item Offer and don't apply it in the Wizard
          //this creates an inconsistency in where we have an Offer selected but not applied
          if (appliedOffers.length === 0 && selectedLoyaltyOffer) {
            dispatch(actions.loyalty.setSelectedOffer(null));
          }

          await prepareCartEntries({
            store,
            newStore,
            requestedServiceMode: finalServiceMode,
          });

          callback?.();

          if (shouldRequestCheckoutReprice || shouldRequestDeliveryReprice) {
            setCheckoutRepriceRequestId(current => current + 1);
          }
        },
        requestedServiceMode: finalServiceMode,
        // TODO: remove this parameter from selectStore
        selectedOffer: null,
      });

      setFetchingPosData(false);
    },
    [
      selectStore,
      enableMinimalCartUi,
      isCartEmpty,
      minimalCartEntries.length,
      appliedOffers.length,
      selectedLoyaltyOffer,
      prepareCartEntries,
      store,
      dispatch,
    ]
  );

  const shouldHandleReorderRef = useRef(false);
  const setShouldHandleReorder = useCallback((bool: boolean) => {
    shouldHandleReorderRef.current = bool;
  }, []);

  useEffect(() => {
    if (!shouldHandleReorderRef.current) {
      return;
    }
    // we have to wait for pricing to be complete from selecting a new store before we redirect
    // we also only want to reorder if we haven't already, pendingReorder gets set to null when we are done reordering
    if (isStoreOpenAndAvailable && prices && pendingReorder) {
      setShouldHandleReorder(false);
      handleReorder(pendingReorder);
    }
  }, [handleReorder, isStoreOpenAndAvailable, pendingReorder, prices, setShouldHandleReorder]);

  const reorder: IOrderContext['reorder'] = {
    handleReorder,
    resetPendingReorder,
    reordering,
    pendingReorder,
    setReordering,
    setPendingReorder,
    reorderedOrderId,
    setShouldHandleReorder,
  };

  const recent = {
    pendingRecentItem,
    setPendingRecentItem,
    unavailablePendingRecentItem,
    setUnavailablePendingRecentItem,
  };

  const value = ({
    unavailableCartEntries,
    setUnavailableCartEntries,
    serverOrder,
    alertOrderDeliveryMinimum,
    clearServerOrder,
    clearUserOwnedOrderState,
    selectOrderStore,
    fetchingPosData,
    clearCartStoreServiceModeTimeout,
    logCartStoreAndTimeout,
    deliveryAddress,
    setDeliveryAddress,
    deliveryInstructions,
    setDeliveryInstructions,
    orderPhoneNumber,
    setOrderPhoneNumber,
    pickupDate,
    setPickupDate,
    onCommitSuccess,
    updateShouldSaveDeliveryAddress,
    setQuoteId,
    quoteId,
    checkoutRequestedSubtotalCents,
    setCheckoutRequestedSubtotalCents,
    checkoutRepriceRequestId,
    checkoutRepriceRequestCompletedId,
    setCheckoutRepriceRequestCompletedId,
    fireOrderInXSeconds,
    reorder,
    recent,
    shouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    formShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
    isStoreChangeHydrationInProgress,
  } as unknown) as IOrderContext;

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export default OrderContext.Consumer;
